import { Task } from '@/types/tasks';
import { createDragStartHandler, createDragOverHandler, createDragEndHandler } from './drag-handlers';
import { updateTaskInDatabase } from './task-database-operations';
import { useTaskStateUpdates } from './use-task-state-updates';
import { useCallback, useMemo, useState } from 'react';
import { GoalTaskContextType } from '@/hooks/tasks/use-goal-filtered-tasks';

/**
 * Hook that provides drag-and-drop functionality for goal-specific task boards
 * Now uses immediate local state updates like workbook/storage systems
 */
export const useGoalTaskBoardDrag = (
  setActiveTask: (task: Task | null) => void,
  setShouldUseLocalTasks: (value: boolean) => void,
  goalTaskContext: GoalTaskContextType
) => {
  const { 
    activeTasks, 
    completedTasks, 
    setActiveTasks, 
    setCompletedTasks,
    updateTask,
    resetToFilteredState
  } = goalTaskContext;
  
  const { isUpdating } = useTaskStateUpdates();
  const [isDragUpdating, setIsDragUpdating] = useState(false);
  
  // Create unified database update function that uses goal context's enhanced updateTask
  const updateTaskInDatabase = useCallback(async (taskId: string, updates: Partial<Task>) => {
    console.log('[GoalTaskBoardDrag] Database update via goal context:', { taskId, updates });
    // Use goal context's enhanced updateTask which already handles immediate local updates
    updateTask(taskId, updates, true); // silent=true for drag operations
  }, [updateTask]);
  
  // Memoize drag handlers to prevent unnecessary re-renders
  const handleDragStart = useCallback((event: any) => {
    // Use the factory-created handler with goal-specific task arrays
    createDragStartHandler(
      setActiveTask,
      activeTasks,
      completedTasks
    )(event);
  }, [setActiveTask, activeTasks, completedTasks]);
  
  // Create memoized drag over handler with goal-specific task arrays
  const handleDragOver = useMemo(() => 
    createDragOverHandler(activeTasks, completedTasks),
    [activeTasks, completedTasks]
  );
  
  const handleDragEnd = useCallback(async (event: any) => {
    setIsDragUpdating(true);
    
    try {
      // Use the factory-created handler with goal context's state setters
      await createDragEndHandler(
        setActiveTask,
        activeTasks,
        completedTasks,
        setActiveTasks,
        setCompletedTasks,
        updateTaskInDatabase
      )(event);
    } finally {
      setIsDragUpdating(false);
      
      // Reset to filtered state after a delay to allow database updates to complete
      setTimeout(() => {
        resetToFilteredState();
      }, 500);
    }
  }, [
    setActiveTask,
    activeTasks,
    completedTasks,
    setActiveTasks,
    setCompletedTasks,
    updateTaskInDatabase,
    resetToFilteredState
  ]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    isUpdating: isUpdating || isDragUpdating
  };
}; 